/* Optional category ranking. All requests stay on the Shopify storefront. */
(function (root) {
  'use strict';
  const keys = ['filter.p.m.spike_bestsellers.category_handles_a', 'filter.p.m.spike_bestsellers.category_handles_b'];
  const version = 'category-model-units-v1';

  function path(url) {
    const match = url.pathname.match(/^(\/(?:[a-z]{2}(?:-[A-Za-z]{2})?))?\/collections\/([a-z0-9][a-z0-9-]*)(\/.*)?$/);
    if (!match || match[3]?.startsWith('/products/')) return null;
    return { locale: match[1] || '', handle: match[2], tail: match[3] || '' };
  }

  function valid(config, pageUrl) {
    const page = path(new URL(pageUrl));
    const m = config?.manifest;
    return Boolean(page && page.handle === config?.source && m?.version === version &&
      ['a', 'b'].includes(m.slot) && m.handle === `spike-category-ranking-${m.slot}` &&
      Array.isArray(m.sources) && m.sources.includes(page.handle));
  }

  function requestUrl(value, pageUrl, config) {
    const page = new URL(pageUrl);
    const url = new URL(value, page);
    const source = path(page);
    const requested = path(url);
    if (!valid(config, pageUrl) || url.origin !== page.origin || !requested ||
      requested.handle !== source.handle || url.searchParams.get('sort_by') !== 'best-selling') return null;
    url.pathname = `${requested.locale}/collections/${config.manifest.handle}${requested.tail}`;
    keys.forEach(key => url.searchParams.delete(key));
    url.searchParams.set(keys[config.manifest.slot === 'a' ? 0 : 1], source.handle);
    url.searchParams.set('sort_by', 'manual');
    return url;
  }

  function customerUrl(value, pageUrl, config) {
    if (!valid(config, pageUrl)) return value;
    const page = new URL(pageUrl);
    const source = path(page);
    const helper = `${source.locale}/collections/${config.manifest.handle}`;
    const helperBase = new URL(page);
    helperBase.pathname = helper + source.tail;
    const url = new URL(value, helperBase);
    if (url.origin !== page.origin || (url.pathname !== helper && !url.pathname.startsWith(`${helper}/`))) return value;
    const tail = url.pathname.slice(helper.length);
    url.pathname = `${source.locale}/collections/${source.handle}${tail}`;
    keys.forEach(key => url.searchParams.delete(key));
    if (!tail.startsWith('/products/') && (!url.searchParams.has('sort_by') || url.searchParams.get('sort_by') === 'manual')) {
      url.searchParams.set('sort_by', 'best-selling');
    }
    return `${url.pathname}${url.search}${url.hash}`;
  }

  // Form controls also contain the default sort. Filtering must not turn a
  // collection whose default is best-selling into an explicit custom selection.
  function formParams(value, pageUrl, sortSelection = false) {
    const params = new URLSearchParams(value);
    const page = new URL(pageUrl);
    if (!sortSelection && !page.searchParams.has('sort_by')) params.delete('sort_by');
    for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
      if (!params.has(key) && page.searchParams.has(key)) params.set(key, page.searchParams.get(key));
    }
    if (page.searchParams.get('view') === 'spike-sale') params.set('view', 'spike-sale');
    keys.forEach(key => params.delete(key));
    return params.toString();
  }

  function prepareHTML(html, pageUrl, config, parse) {
    const doc = parse(html);
    const context = doc.querySelector('[data-spike-category-context]');
    let data;
    try { data = JSON.parse(context?.textContent); } catch { /* checked below */ }
    if (data?.version !== 1 || data.handle !== config.manifest.handle || data.source !== config.source ||
      data.sort !== 'manual' || !doc.querySelector('#CollectionProductGrid .collection')) {
      throw new Error('Category index response did not confirm its source and native filter');
    }
    doc.querySelectorAll('[href], [data-href], form[action]').forEach(element => {
      for (const attribute of ['href', 'data-href', 'action']) {
        const value = element.getAttribute(attribute);
        if (!value || /^(?:javascript:|mailto:|tel:|#)/i.test(value)) continue;
        element.setAttribute(attribute, customerUrl(value, pageUrl, config));
      }
    });
    doc.querySelectorAll('[name="sort_by"]').forEach(select => {
      select.querySelectorAll('option').forEach(option => option.toggleAttribute('selected', option.value === 'best-selling'));
      select.value = 'best-selling';
    });
    return doc.documentElement.outerHTML;
  }

  // CommonJS is only used by local tests; the theme loads this as a deferred asset.
  const pure = { requestUrl, customerUrl, formParams, prepareHTML, valid };
  if (typeof module !== 'undefined' && module.exports) module.exports = pure;
  if (!root?.document) return;

  let config;
  try { config = JSON.parse(root.document.getElementById('spike-category-bestsellers-config')?.textContent); } catch { config = null; }
  const api = root.SpikeCategoryBestsellers = {
    requestUrl: url => requestUrl(url, root.location.href, config),
    handles: url => Boolean(root.SpikeCategorySale?.handles(url, root.location.href, config) || requestUrl(url, root.location.href, config) || root.SpikeCategoryPreview?.handles(url, root.location.href, config)),
    formParams: (params, explicitSort) => formParams(params, root.location.href, explicitSort),
    enabled: () => config?.sale === true || valid(config, root.location.href) || Boolean(root.SpikeCategoryPreview?.valid(config, root.location.href)),
    async load(url, options = {}) {
      const customer = new URL(url, root.location.href);
      // Keep draft preview on requests produced by forms and pagination.
      for (const key of ['preview_theme_id', '_ab', '_fd', '_sc']) {
        const current = new URL(root.location.href).searchParams;
        if (!customer.searchParams.has(key) && current.has(key)) customer.searchParams.set(key, current.get(key));
      }
      const sale = root.SpikeCategorySale?.handles(customer, root.location.href, config);
      const target = sale ? null : requestUrl(customer, root.location.href, config);
      const native = new URL(customer);
      // Sale pages are paginated after intersection, independently of the native category page.
      if (sale) native.searchParams.set('page', '1');
      const response = await root.fetch(target || native, { ...options, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection request failed (${response.status})`);
      const html = await response.text();
      if (sale) return root.SpikeCategorySale.load(customer, html, config, options);
      if (target) return prepareHTML(html, customer, config, text => new DOMParser().parseFromString(text, 'text/html'));
      if (root.SpikeCategoryPreview?.handles(customer, root.location.href, config)) {
        return root.SpikeCategoryPreview.load(customer, html, config, options);
      }
      return html;
    },
    syncSort() {
      root.SpikeCategorySale?.sync();
      const selected = new URL(root.location.href).searchParams.get('sort_by') || config?.defaultSort;
      if (!selected) return;
      root.document.querySelectorAll('#CollectionFiltersForm [name="sort_by"]').forEach(select => { select.value = selected; });
      root.document.querySelectorAll('[data-sorting]').forEach(group => {
        let text;
        group.querySelectorAll('[data-sort-by-item]').forEach(item => {
          const label = item.querySelector('[data-href]');
          const active = label?.dataset.href === selected;
          item.classList.toggle('is-active', active);
          if (active) text = label.textContent.trim();
        });
        if (text && group.querySelector('.label-text')) group.querySelector('.label-text').textContent = text;
      });
    },
    clearError() {
      root.document.getElementById('spike-category-load-error')?.remove();
    },
    showError() {
      api.clearError();
      const message = root.document.createElement('p');
      message.id = 'spike-category-load-error';
      message.setAttribute('role', 'alert');
      message.textContent = config?.errorMessage || 'Produkty se nepodařilo načíst. Zkuste to prosím znovu.';
      root.document.getElementById('CollectionProductGrid')?.prepend(message);
      root.SpikeCategorySale?.failed(message);
    },
  };

  const start = () => {
    if (!api.handles(root.location.href) || !root.document.querySelector('collection-filters-form')) return;
    root.customElements.whenDefined('collection-filters-form').then(() => {
      root.customElements.get('collection-filters-form').renderPage(root.location.search.slice(1), null, false);
    });
  };
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})(typeof window === 'undefined' ? null : window);
